/**
 * FoodLookup — find a food by barcode or by name, and hand back its macros.
 *
 * TWO SOURCES BECAUSE THEY FAIL AT OPPOSITE THINGS. A barcode goes to Open Food Facts and
 * returns the sodium of the actual package, which no reference table can match. A name goes
 * to USDA FoodData Central, which is where calories and fat for whole foods live — the gap
 * health-system.html leaves open.
 *
 * SCANNING IS PROGRESSIVE ENHANCEMENT. BarcodeDetector exists in Chrome and Android but not
 * in Safari, so on an iPhone the camera button is simply absent and the digits are typed
 * instead. A camera button that silently does nothing on the one device he actually carries
 * would be worse than no button, so availability is detected rather than assumed.
 *
 * NOTHING IS SAVED FROM HERE. A hit is handed to the caller to review and edit before it
 * becomes a recipe or a meal — same confirm-before-store rule the PRD sets for the AI
 * resolver, and for the same reason: an external database is an estimate, not a measurement.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { macro } from './format';

export interface FoodHit {
  source: 'cache' | 'openfoodfacts' | 'usda' | 'user';
  key: string;
  name: string;
  brand: string | null;
  barcode: string | null;
  portion_desc: string;
  calories: number | null;
  protein_g: number | null;
  fat_g: number | null;
  carb_g: number | null;
  sodium_mg: number | null;
  potassium_mg: number | null;
}

const SOURCE_LABEL: Record<FoodHit['source'], string> = {
  openfoodfacts: 'the package',
  usda: 'USDA',
  cache: 'looked up before',
  user: 'your correction',
};

/** Chrome/Android only. Safari has no BarcodeDetector, so the camera button hides itself. */
function scannerAvailable(): boolean {
  return typeof window !== 'undefined' && 'BarcodeDetector' in window;
}

export default function FoodLookup({ onPick }: { onPick: (hit: FoodHit) => void }) {
  const [mode, setMode] = useState<'name' | 'barcode'>('name');
  const [q, setQ] = useState('');
  const [code, setCode] = useState('');
  const [hits, setHits] = useState<FoodHit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  const search = async (term = q) => {
    if (term.trim().length < 2) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.get<FoodHit[]>(`/wellness/foods/search?q=${encodeURIComponent(term)}`);
      setHits(r);
      if (!r.length) setMsg('Nothing found. Try a simpler word, or enter it by hand.');
    } catch {
      setMsg('The lookup did not answer. You can still enter it by hand.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Search as you type, debounced. Nobody hunting for a food on a phone wants to find the
   * Look up button, and relying on Enter is worse — the on-screen keyboard's return key is
   * inconsistent across browsers. The button stays for anyone who reaches for it.
   *
   * 450 ms is long enough that "sirloin" is one request rather than seven, which matters
   * because USDA's shared demo key is throttled to roughly thirty an hour.
   */
  useEffect(() => {
    if (mode !== 'name') return;
    const term = q.trim();
    if (term.length < 2) {
      setHits(null);
      return;
    }
    const t = setTimeout(() => void search(term), 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, mode]);

  const byBarcode = async (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 6) return;
    setBusy(true);
    setMsg(null);
    try {
      const hit = await api.get<FoodHit>(`/wellness/foods/barcode/${digits}`);
      setHits([hit]);
    } catch {
      // 404 is the common, meaningful case: crowd-sourced data does not have everything.
      setMsg('That barcode is not in Open Food Facts. Enter it by hand and it will be saved.');
      setHits([]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-canvas p-3">
      <div className="mb-2 flex gap-1">
        {(
          [
            ['name', 'By name'],
            ['barcode', 'Barcode'],
          ] as const
        ).map(([m, label]) => (
          <button
            key={m}
            type="button"
            onClick={() => {
              setMode(m);
              setHits(null);
              setMsg(null);
            }}
            aria-pressed={mode === m}
            className={`min-h-9 rounded-full px-3 text-xs font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
              mode === m ? 'bg-ink text-canvas' : 'border border-border bg-surface text-ink-soft'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'name' ? (
        <div className="flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void search();
              }
            }}
            placeholder="sirloin, spinach, egg…"
            className="min-h-11 min-w-0 flex-1 rounded-xl border border-border bg-surface px-3 text-ink placeholder:text-ink-faint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          />
          <button
            type="button"
            onClick={() => void search()}
            disabled={busy}
            className="min-h-11 shrink-0 rounded-full border border-sage bg-surface px-4 text-sm font-medium text-sage disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            {busy ? '…' : 'Look up'}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void byBarcode(code);
                }
              }}
              inputMode="numeric"
              placeholder="Type the digits under the barcode"
              className="min-h-11 min-w-0 flex-1 rounded-xl border border-border bg-surface px-3 text-ink placeholder:text-ink-faint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            />
            <button
              type="button"
              onClick={() => void byBarcode(code)}
              disabled={busy}
              className="min-h-11 shrink-0 rounded-full border border-sage bg-surface px-4 text-sm font-medium text-sage disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              {busy ? '…' : 'Look up'}
            </button>
          </div>
          {scannerAvailable() ? (
            <button
              type="button"
              onClick={() => setScanning(true)}
              className="min-h-11 w-full rounded-full border border-border bg-surface text-sm font-medium text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              Scan with the camera
            </button>
          ) : (
            <p className="text-xs text-ink-soft">
              Camera scanning needs Chrome or Android — Safari has no barcode support, so on
              an iPhone type the digits.
            </p>
          )}
        </div>
      )}

      {scanning && (
        <Scanner
          onClose={() => setScanning(false)}
          onCode={(c) => {
            setScanning(false);
            setCode(c);
            void byBarcode(c);
          }}
        />
      )}

      {msg && <p className="mt-2 text-xs text-honey">{msg}</p>}

      {hits && hits.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {hits.map((h) => (
            <li key={`${h.source}-${h.key}`}>
              <button
                type="button"
                onClick={() => onPick(h)}
                className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-left hover:border-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
              >
                <span className="block text-sm font-medium text-ink">
                  {h.name}
                  {h.brand && <span className="text-ink-soft"> · {h.brand}</span>}
                </span>
                <span className="block text-xs text-ink-soft">
                  per {h.portion_desc} · {macro(h.calories)} kcal · {macro(h.protein_g, 'g')}{' '}
                  protein · {macro(h.sodium_mg, 'mg')} sodium
                </span>
                {/* Provenance is shown, because these are not equally trustworthy and
                    sodium is the number the salt test reads. */}
                <span className="block text-xs text-ink-faint">from {SOURCE_LABEL[h.source]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Camera scanner. Only mounted where BarcodeDetector exists. */
function Scanner({ onCode, onClose }: { onCode: (c: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (stopped) return;
        if (video.current) {
          video.current.srcObject = stream;
          await video.current.play();
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Detector = (window as any).BarcodeDetector;
        const detector = new Detector({
          formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128'],
        });

        const tick = async () => {
          if (stopped || !video.current) return;
          try {
            const found = await detector.detect(video.current);
            if (found?.length && found[0].rawValue) {
              onCode(String(found[0].rawValue));
              return;
            }
          } catch {
            /* a frame that cannot be decoded is normal, keep going */
          }
          raf = requestAnimationFrame(() => void tick());
        };
        void tick();
      } catch {
        setErr('Could not open the camera. Type the digits instead.');
      }
    })();

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [onCode]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/80 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-surface p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-ink">Scan a barcode</h3>
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 rounded-full px-3 text-sm text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            Close
          </button>
        </div>
        {err ? (
          <p className="py-6 text-center text-sm text-clay">{err}</p>
        ) : (
          <video ref={video} playsInline muted className="w-full rounded-xl bg-ink" />
        )}
      </div>
    </div>
  );
}
